import { usePlatform } from "components/utils/PlatformContext";
import { useEffect, useState } from "react";
import { updateRecordRequest } from "services/apiRequests";
import { ClassListType } from "utils/interfaces";
import { singlify } from "utils/utils";
import BackButtonHeaderPopUp from "../common/BackButtonHeader";

interface SetPasswordPanelProps {
  selectedData?: ClassListType;
  externalSetPopUpState?: React.Dispatch<
    React.SetStateAction<"main" | "add-teacher" | "set-password">
  >;
  afterSetClassPassword?: () => void;
}

const SetPasswordPanel: React.FC<SetPasswordPanelProps> = ({
  selectedData,
  externalSetPopUpState,
  afterSetClassPassword,
}) => {
  // States definition
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState(false);

  const { classesTerminology } = usePlatform();

  /**
   * Function used to check if the pair of passwords are the same
   * and pass the security tests
   */
  useEffect(() => {
    setPasswordCheck(!!password && !!rePassword && password === rePassword);
  }, [password, rePassword]);

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    if (externalSetPopUpState) externalSetPopUpState("main");
  };

  /**
   * Function used to set the password to the whole class
   */
  const handleSavePassword = async () => {
    // Send the password for every student in this class
    const response = await updateRecordRequest(
      {
        classId: selectedData ? selectedData._id : "",
        newPassword: password,
      },
      "/api/classes/set-password",
    );

    // Check if the request
    if (!response.successful) {
      alert(response.message);
      return;
    }

    if (afterSetClassPassword) afterSetClassPassword();

    // And close the pop up
    if (externalSetPopUpState) externalSetPopUpState("main");
  };

  return (
    <div className="relative w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
      <div className="self-stretch justify-between items-center inline-flex">
        <div className="justify-start items-center gap-3 flex">
          <BackButtonHeaderPopUp onClick={handlePressBack} />
          <div className="text-neutral-600 text-lg font-normal font-sans leading-snug">
            Set a password for this {singlify(classesTerminology).toLowerCase()}
          </div>
        </div>
      </div>
      <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
        <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
          <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide">
            Password
          </div>
          <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
              placeholder="Password"
            />
          </div>
        </div>
        <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
          <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide">
            Confirm password
          </div>
          <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
            <input
              type="password"
              value={rePassword}
              onChange={(event) => setRePassword(event.target.value)}
              className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
              placeholder="Repeat the password"
            />
          </div>
        </div>
      </div>
      <div
        className={
          passwordCheck
            ? "self-end w-full h-fit px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
            : "self-end w-full h-fit px-5 py-3 bg-Subtle rounded justify-center items-center gap-2 inline-flex cursor-not-allowed"
        }
        onClick={handleSavePassword}
      >
        <div
          className={
            passwordCheck
              ? "text-white text-lg font-semibold font-sans leading-snug"
              : "text-neutral-400 text-lg font-semibold font-sans leading-snug"
          }
        >
          Save Password
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPanel;
